import { useDealerContext } from "@porsche-customer/slfinrtl-context"
import type { PermissionId, Permissions } from "@porsche-customer/slfinrtl-context-types"
import type { Environment } from "@porsche-customer/slfinrtl-environment"
import { useMemo } from "react"
import { useConfig } from "src/hooks"
import { getFeatureToggle } from "../getConfig"
import { isUsingPcw20Website } from "../getPcw20Host"
import type { AddOfferQueryParams } from "./buildAddOfferUrl"
import { buildAddOfferUrl } from "./buildAddOfferUrl"
import type { ConfiguratorLoadConfigurationParams } from "./buildConfiguratorLoadConfigurationUrl"
import { buildConfiguratorLoadConfigurationUrl } from "./buildConfiguratorLoadConfigurationUrl"
import type { ConfiguratorModelSelectionParams } from "./buildConfiguratorModelSelectionUrl"
import { buildConfiguratorModelSelectionUrl } from "./buildConfiguratorModelSelectionUrl"
import type { EditOfferQueryParams } from "./buildEditOfferUrl"
import { buildEditOfferUrl } from "./buildEditOfferUrl"
import { legacyDeliveredVehicleDetailsRoute, usedDeliveredVehicleDetailsRoute } from "./deliveredVehicleDetailsRoutes"
import type { DealerAppRoute } from "./route"
import { route } from "./route"
import type { DealerAppRouteWithVinQuery } from "./routeWithVinQuery"
import { routeWithVinQuery } from "./routeWithVinQuery"
import { useDealerAppNavigationType } from "./useDealerAppNavigationType"

const paddockHostByEnvironment: Record<Environment, string> = {
	dev: "https://paddock-int.dpp.porsche.com",
	local: "https://paddock-int.dpp.porsche.com",
	preview: "https://paddock-dev.dpp.porsche.com",
	prod: "https://paddock.dpp.porsche.com",
}

// The real shape produced by ifPermitted: either the full route, or a disabled placeholder with no path/url at all.
// `disabledHint` is kept optional on the enabled branch too, so consumers can access it via plain optional
// chaining (`route?.disabledHint`) without narrowing the union first.
type PermissionChecked<T> =
	| (T & { disabledHint?: undefined })
	| ({ disabledHint: string } & { [K in keyof T]?: undefined })
	| undefined

// Read-only tabs shared by every place vehicleDetails is reachable from (inventoryList, the legacy
// deliveredVehiclesList, and fulfillmentDeliveredVehiclesList.usedVehicles) - see vehicleDetailsTabs().
export interface VehicleDetailsTabs {
	buildsheet?: DealerAppRoute
	sales?: PermissionChecked<DealerAppRoute>
	vehicleHistory: DealerAppRoute
}

const mediaList = {
	...route("/media-list"),
	vehicleMediaEdit: {
		path: "/media-list/:vehicleKey",
		url: (vehicleKey: string) => `/media-list/${vehicleKey}`,
	} satisfies DealerAppRoute,
}

interface DealerAppMediaRoutes {
	mediaList: DealerAppRoute & {
		vehicleMediaEdit: DealerAppRoute
	}
}

// Fields marked `?` here are genuinely conditional: gated by a marketplace-config feature flag (via ifEnabled)
// and/or a user permission (via ifPermitted/PermissionChecked). Fields without `?` are constructed
// unconditionally and are therefore always present whenever DealerAppInventoryRoutes is the returned shape.
interface DealerAppInventoryRoutes extends DealerAppMediaRoutes {
	analytics?: DealerAppRoute
	buildsheets?: DealerAppRouteWithVinQuery
	deliveredVehiclesList?: PermissionChecked<
		DealerAppRoute & {
			vehicleDetails: DealerAppRoute & VehicleDetailsTabs
		}
	>
	external: {
		configurator: {
			url: (params: ConfiguratorModelSelectionParams) => string
		}
		configuratorLoadConfiguration: {
			url: (params: ConfiguratorLoadConfigurationParams) => string
		}
		oneVms: {
			connectSaleToOrder: {
				url: (saleId: string) => string
			}
			orderDetails: {
				url: (newCarOrderId: string) => string
			}
			salesChangeRequest: {
				url: (saleId: string) => string
			}
		}
		pcss: {
			url: (vin?: string) => string
		}
	}
	fulfillmentDeliveredVehiclesList?: DealerAppRoute & {
		saleDetails: DealerAppRoute & {
			saleHistory: DealerAppRoute
		}
		usedVehicles: DealerAppRoute & {
			vehicleDetails: DealerAppRoute & VehicleDetailsTabs
		}
	}
	home: DealerAppRoute
	inquiryContext: DealerAppRoute
	inventoryIntegrationMonitor?: DealerAppRoute
	inventoryList: DealerAppRoute & {
		vehicleDetails: DealerAppRoute &
			VehicleDetailsTabs & {
				// action-flow urls
				add: DealerAppRoute
				deliver: DealerAppRoute
				editOffer: DealerAppRoute
				mediaEdit: DealerAppRoute
				remove: DealerAppRoute
				reserve: DealerAppRoute
				sell?: PermissionChecked<DealerAppRoute>
				transfer: DealerAppRoute
			}
	}
	notificationCenter?: DealerAppRoute & {
		group: DealerAppRoute
	}
	offbrandInventoryList?: DealerAppRoute
	offers: {
		addOffer?: PermissionChecked<{
			path: string
			url: (query: AddOfferQueryParams) => string
		}>
		editOffer?: PermissionChecked<{
			path: string
			url: (offerId: string, query?: EditOfferQueryParams) => string
		}>
		offerDetails?: PermissionChecked<DealerAppRoute>
		offerOverview?: PermissionChecked<DealerAppRoute>
		reportSaleForOffer?: PermissionChecked<DealerAppRoute>
		updateSaleForOffer?: PermissionChecked<DealerAppRoute>
	}
	onlineReservations?: DealerAppRoute
	purchaseRequests?: DealerAppRoute & {
		details: DealerAppRoute
		hidden: DealerAppRoute
		withOwnOffers: DealerAppRoute
	}
	// TODO FRD-4711 remove after Salesforce PoC
	salesforcePoc: DealerAppRoute
	salesList?: DealerAppRoute & {
		saleDetails: DealerAppRoute & {
			deliver: DealerAppRoute
			saleHistory: DealerAppRoute
		}
	}
	settings: {
		account?: PermissionChecked<DealerAppRoute>
		contractualAgreements?: PermissionChecked<
			DealerAppRoute & {
				add: DealerAppRoute
				details: DealerAppRoute
			}
		>
		dealerContactDetails?: DealerAppRoute
		dealerFeesAndAccessories?: DealerAppRoute
		extraCosts?: PermissionChecked<
			DealerAppRoute & {
				add: DealerAppRoute
				specification: DealerAppRoute
			}
		>
		inventoryLinks?: DealerAppRoute
		notificationSettings?: DealerAppRoute
	}
	tradeInRequests?: DealerAppRoute & {
		details: DealerAppRoute
	}
	vehicleHistory?: PermissionChecked<DealerAppRouteWithVinQuery>
	vehicleInformation?: DealerAppRouteWithVinQuery & {
		monroneyLabel?: DealerAppRouteWithVinQuery
		vehicleHistory?: PermissionChecked<DealerAppRouteWithVinQuery>
	}
}

// The hook returns either DealerAppMediaRoutes or DealerAppInventoryRoutes depending on navigationType, so
// consumers that don't know which one they got must treat every field as optional — except mediaList, which
// both branches construct unconditionally and is therefore always present.
export type DealerAppRoutes = DealerAppMediaRoutes & Partial<DealerAppInventoryRoutes>

export const useDealerAppRoutes = (): DealerAppRoutes | null => {
	const navigationType = useDealerAppNavigationType()
	const config = useConfig()
	const { dealershipId, environment, marketplaceKey, permissions } = useDealerContext()

	const mediaRoutes = useMemo(() => {
		return { mediaList } satisfies DealerAppMediaRoutes
	}, [])

	const inventoryRoutes = useMemo(() => {
		const isVehicleInformationEnabled = config.vehicleInformationEnabled
		const isOffersEnabled = config.offers.offerMode !== "DISABLED"

		const paddockHost = paddockHostByEnvironment[environment ?? "dev"]

		const ifOffersEnabledAndManagePermitted = <T>(value: T) =>
			ifEnabled(isOffersEnabled, ifPermitted(permissions.manageOffers, value))

		// Shared tabs for every place vehicleDetails is reachable from (inventoryList, the legacy
		// deliveredVehiclesList, and fulfillmentDeliveredVehiclesList.usedVehicles) - each entry point
		// only needs the read-only tabs, never the action-flow urls (those aren't offered from
		// delivered/used-vehicle contexts, see useDeliveredVehicleActionMenu/UsedDeliveredVehiclesListRowModelDisplay).
		const vehicleDetailsTabs = (basePath: string) => ({
			buildsheet: ifEnabled(config.buildsheetsEnabled, {
				path: "buildsheet",
				url: (vehicleKey: string) => `${basePath}/${vehicleKey}/buildsheet`,
			}),
			sales: ifPermitted(permissions.canViewSalesPrices, {
				path: "sales",
				url: (vehicleKey: string) => `${basePath}/${vehicleKey}/sales`,
			}),
			vehicleHistory: {
				path: "vehicle-history",
				url: (vehicleKey: string) => `${basePath}/${vehicleKey}/vehicle-history`,
			},
		})

		const addOfferRoute = ifOffersEnabledAndManagePermitted({
			path: "/offers/add",
			url: buildAddOfferUrl,
		})
		const editOfferRoute = ifOffersEnabledAndManagePermitted({
			path: "/offers/:offerId/edit",
			url: buildEditOfferUrl,
		})

		return {
			analytics: ifEnabled(config.analyticsEnabled && !config.carSalesLiteEnabled, route("/analytics")),
			buildsheets: ifEnabled(
				config.buildsheetsEnabled && !isVehicleInformationEnabled,
				routeWithVinQuery("/buildsheets"),
			),
			deliveredVehiclesList: ifEnabled(
				config.fulfillmentSalesEnabled === "disabled",
				ifPermitted(permissions.canViewSalesPrices, {
					...route("/delivered-vehicles"),
					vehicleDetails: {
						...legacyDeliveredVehicleDetailsRoute,
						...vehicleDetailsTabs("/delivered-vehicles"),
					},
				}),
			),
			external: {
				configurator: {
					url: (params: ConfiguratorModelSelectionParams) =>
						buildConfiguratorModelSelectionUrl(params, {
							getOfferAddPath: addOfferRoute?.url,
							origin: window.location.origin,
						}),
				},
				configuratorLoadConfiguration: {
					url: (params: ConfiguratorLoadConfigurationParams) =>
						buildConfiguratorLoadConfigurationUrl(params, {
							getOfferAddPath: addOfferRoute?.url,
							getOfferEditPath: editOfferRoute?.url,
							origin: window.location.origin,
						}),
				},
				oneVms: {
					connectSaleToOrder: {
						url: (saleId: string) => `${paddockHost}/cora/lists/open-sales/create-kf/${saleId}`,
					},
					orderDetails: {
						url: (newCarOrderId: string) => `${paddockHost}/dori/?newCarOrderId=${newCarOrderId}`,
					},
					salesChangeRequest: {
						url: (saleId: string) =>
							`${paddockHost}/cora/lists/sales-change-requests/change-request/${saleId}`,
					},
				},
				pcss: {
					url: (vin?: string) => {
						const isProd = environment === "prod" || environment === "preview"
						const baseUrl = isProd
							? "https://pcss.porsche.com/static/vehicle/vehicle"
							: "https://staging.pcss.porsche.com/static/vehicle/vehicle"
						return `${baseUrl}${vin ? `;vin=${vin}` : ""}`
					},
				},
			},
			fulfillmentDeliveredVehiclesList: ifEnabled(config.fulfillmentSalesEnabled !== "disabled", {
				...route("/delivered-vehicles"),
				saleDetails: {
					path: "/delivered-vehicles/:saleId",
					url: (saleId: string) => `/delivered-vehicles/${saleId}`,

					saleHistory: {
						path: "history",
						url: (saleId: string) => `/delivered-vehicles/${saleId}/history`,
					},
				},
				usedVehicles: {
					...route("/delivered-vehicles/used"),
					vehicleDetails: {
						...usedDeliveredVehicleDetailsRoute,
						...vehicleDetailsTabs("/delivered-vehicles/used"),
					},
				},
			}),
			home: route("/"),
			inquiryContext: {
				path: "/inquiry/:inquiryId",
				url: (inquiryId: string) => `/inquiry/${inquiryId}`,
			},
			inventoryIntegrationMonitor: ifEnabled(
				config.inventoryIntegrationMonitorEnabled,
				route("/inventory-integration-monitor"),
			),
			inventoryList: {
				...route("/inventory"),
				vehicleDetails: {
					path: "/inventory/:vehicleKey",
					url: (vehicleKey: string) => `/inventory/${vehicleKey}`,

					// tabs
					...vehicleDetailsTabs("/inventory"),

					// action-flow urls
					add: {
						path: "/inventory/:vehicleKey/add",
						url: (vehicleKey: string) => `/inventory/${vehicleKey}/add`,
					},
					deliver: {
						path: "/inventory/:vehicleKey/deliver",
						url: (vehicleKey: string) => `/inventory/${vehicleKey}/deliver`,
					},
					editOffer: {
						path: "/inventory/:vehicleKey/offer/:offerKey",
						url: (vehicleKey: string, offerKey: string) => `/inventory/${vehicleKey}/offer/${offerKey}`,
					},
					mediaEdit: {
						path: "/inventory/:vehicleKey/media",
						url: (vehicleKey: string) => `/inventory/${vehicleKey}/media`,
					},
					remove: {
						path: "/inventory/:vehicleKey/remove",
						url: (vehicleKey: string) => `/inventory/${vehicleKey}/remove`,
					},
					reserve: {
						path: "/inventory/:vehicleKey/reserve",
						url: (vehicleKey: string) => `/inventory/${vehicleKey}/reserve`,
					},
					sell: ifPermitted(permissions.canViewSalesPrices, {
						path: "/inventory/:vehicleKey/sell",
						url: (vehicleKey: string) => `/inventory/${vehicleKey}/sell`,
					}),
					transfer: {
						path: "/inventory/:vehicleKey/transfer",
						url: (vehicleKey: string) => `/inventory/${vehicleKey}/transfer`,
					},
				},
			},
			mediaList,
			notificationCenter: ifEnabled(config.notificationCenterEnabled, {
				...route("/notification-center"),
				group: {
					path: "/notification-center/:group",
					url: (group: string) => `/notification-center/${group}`,
				},
			}),
			offbrandInventoryList:
				marketplaceKey === "us"
					? ifEnabled(isUsingPcw20Website(dealershipId), route("/offbrand-inventory"))
					: undefined,
			offers: {
				addOffer: addOfferRoute,
				editOffer: editOfferRoute,
				offerDetails: ifOffersEnabledAndManagePermitted({
					path: "/offers/:offerId",
					url: (offerId: string) => `/offers/${offerId}`,
				}),
				offerOverview: ifOffersEnabledAndManagePermitted(route("/offers")),
				reportSaleForOffer: ifEnabled(
					isOffersEnabled && config.fulfillmentSalesEnabled === "enabled",
					ifPermitted(permissions.canReportSales, {
						path: "/sales/report-sale/:offerId",
						url: (offerId: string) => `/sales/report-sale/${offerId}`,
					}),
				),
				updateSaleForOffer: ifEnabled(
					isOffersEnabled,
					ifPermitted(permissions.canReportSales, {
						path: "/sales/update-sale/:offerId",
						url: (offerId: string) => `/sales/update-sale/${offerId}`,
					}),
				),
			},
			onlineReservations: ifEnabled(
				config.onlineOrderingEnabled && !config.carSalesLiteEnabled,
				route("/online-reservations"),
			),
			purchaseRequests: ifEnabled(config.purchaseRequestsEnabled && !config.carSalesLiteEnabled, {
				...route("/purchase-requests"),
				details: {
					path: "/purchase-requests/:id",
					url: (id: string) => `/purchase-requests/${id}`,
				},
				hidden: route("/purchase-requests/hidden"),
				withOwnOffers: route("/purchase-requests/with-own-offers"),
			}),
			salesforcePoc: {
				path: "/salesforce-poc/:correlationId",
				url: (correlationId: string) => `/salesforce-poc/${correlationId}`,
			},
			salesList: ifEnabled(config.fulfillmentSalesEnabled !== "disabled", {
				...route("/sales"),
				saleDetails: {
					path: "/sales/:saleId",
					url: (saleId: string) => `/sales/${saleId}`,

					deliver: {
						path: "/sales/:saleId/deliver",
						url: (saleId: string) => `/sales/${saleId}/deliver`,
					},
					saleHistory: {
						path: "history",
						url: (saleId: string) => `/sales/${saleId}/history`,
					},
				},
			}),
			settings: {
				account: ifPermitted(permissions.accountSettings, {
					path: "/settings/account/*",
					url: () => "/settings/account",
				}),
				contractualAgreements: ifEnabled(
					config.contractualAgreementsEnabled,
					ifPermitted(permissions.manageContractualAgreements, {
						...route("/settings/contractual-agreements"),
						add: route("/settings/contractual-agreements/add"),
						details: {
							path: "/settings/contractual-agreements/:contractualAgreementId",
							url: (contractualAgreementId: string) =>
								`/settings/contractual-agreements/${contractualAgreementId}`,
						},
					}),
				),
				dealerContactDetails: ifEnabled(
					getFeatureToggle("integrated-dealer-contact-details-settings"),
					route("/settings/contact-details"),
				),
				dealerFeesAndAccessories: ifEnabled(
					config.dealerFeesAndAccessoriesEnabled,
					route("/settings/fees-and-accessories"),
				),
				extraCosts: ifEnabled(
					config.extraCostsEnabled,
					ifPermitted(permissions.manageExtraCosts, {
						...route("/settings/extra-costs"),
						add: route("/settings/extra-costs/add"),
						specification: {
							path: "/settings/extra-costs/:extraCostSpecificationId",
							url: (extraCostSpecificationId: string) =>
								`/settings/extra-costs/${extraCostSpecificationId}`,
						},
					}),
				),
				inventoryLinks: ifEnabled(
					!isUsingPcw20Website(dealershipId) && !config.carSalesLiteEnabled,
					route("/settings/inventory-links"),
				),
				notificationSettings: ifEnabled(config.notificationCenterEnabled, route("/settings/notifications")),
			},
			tradeInRequests: ifEnabled(config.tradeInEnabled && !config.carSalesLiteEnabled, {
				...route("/trade-in-requests"),
				details: {
					path: "/trade-in-requests/:id",
					url: (id: string) => `/trade-in-requests/${id}`,
				},
			}),
			vehicleHistory: ifEnabled(
				!isVehicleInformationEnabled,
				ifPermitted(permissions.vehicleHistory, routeWithVinQuery("/vehicle-history")),
			),
			vehicleInformation: ifEnabled(isVehicleInformationEnabled, {
				...routeWithVinQuery("/vehicle-information"), // buildsheets
				monroneyLabel: ifEnabled(
					config.monroneyLabelEnabled,
					routeWithVinQuery("/vehicle-information/monroney-label"),
				),
				vehicleHistory: ifPermitted(
					permissions.vehicleHistory,
					routeWithVinQuery("/vehicle-information/vehicle-history"),
				),
			}),
		} satisfies DealerAppInventoryRoutes
	}, [config, dealershipId, environment, marketplaceKey, permissions])

	switch (navigationType) {
		case "MEDIA":
			return mediaRoutes
		case "INVENTORY":
			return inventoryRoutes
		default:
			return null
	}
}

const ifEnabled = <T>(featureFlag: boolean, value: T): T | undefined => (featureFlag ? value : undefined)

const ifPermitted = <T>(permission: Permissions[PermissionId], value: T): PermissionChecked<T> =>
	permission === true
		? (value as PermissionChecked<T>)
		: typeof permission === "string"
			? { disabledHint: permission }
			: undefined
